// ── Landing page ──────────────────────────────────────────────────────────────
//
// pervend.com. The audience is the owner, GM or controller of a regional
// vending operator — somebody who has been pitched by every telemetry vendor
// in the industry and will close the tab in eight seconds if a term is wrong.
// That dictates the whole page:
//
//   * Vernacular is the credential. DEX, par levels, interchange-plus, T+1
//     funding, pre-auth holds. Get them right and they assume you are an
//     operator; get one wrong and you are another software company.
//   * No invented proof. No logos, no testimonials, no "operators save 14%".
//     There is one design partner, and nothing on the page claims more.
//   * The thing for sale TODAY is the diagnostic, not the software. That is a
//     real service that can be delivered by hand this week. The product is
//     described as what it is: being built.
//   * The calculator is the proof of competence — free, no gate, 30 seconds.

import React from "react";
import type { PageProps } from "./types";

interface Question {
  readonly q: string;
  readonly a: string;
}

interface Pillar {
  readonly t: string;
  readonly d: string;
  /** The concrete number. A pillar without one is a brochure line. */
  readonly e: string;
}

interface DiagnosticAsk {
  readonly t: string;
  readonly d: string;
}

const QUESTIONS: readonly Question[] = Object.freeze([
  {
    q: "Is every commission you are owed actually arriving, and correct?",
    a: "Bottler rebates and operator commissions turn up monthly in four different formats, most of them scans. Nobody checks the rate against the agreement, nobody checks the arithmetic, and nobody notices the month a statement simply does not come.",
  },
  {
    q: "What are card fees really costing you, and which products lose money on a card?",
    a: "For most fleets the card bill is several times the telemetry and software spend put together — and about half of it is driven by how often a card is tapped rather than by how much you sell.",
  },
  {
    q: "Which machines and which sites actually make money?",
    a: "You know sales by machine. You know what product cost in total. Almost nobody has put the two together alongside commission paid and the cost of the driver turning up.",
  },
]);

const PILLARS: readonly Pillar[] = Object.freeze([
  {
    t: "Commission assurance",
    d: "Every statement checked against itself and against your agreement — the rate, the base, the arithmetic, and whether it arrived at all. In both directions: what the bottlers and operators owe you, and the commission you owe your own locations every month.",
    e: "A rate one point light on a $15,000 monthly base is $150 a month, at one site, indefinitely. Nothing in a normal month would surface it.",
  },
  {
    t: "Card fee intelligence",
    d: "Your true effective rate by machine, site and product — and the split between what you pay for selling and what you pay per tap. Then the list of prices worth changing, with the annual saving beside each one.",
    e: "A $1.00 item costs about 16¢ to accept on a card. If its margin is 30¢, more than half of that margin goes to the processor.",
  },
  {
    t: "Contribution per machine",
    d: "Sales, less product cost, less card fees, less the commission you pay the facility, less the cost of the visit. Per machine, per month, and per visit — plus the sales you never made, read out of the DEX when a selection sat empty.",
    e: "In any fleet of 500 there is a tail where the margin does not cover the driver turning up. The bottom of that list is the action list.",
  },
]);

const DIAGNOSTIC: readonly DiagnosticAsk[] = Object.freeze([
  { t: "One month of processor settlement detail", d: "The itemised version that shows interchange and markup per transaction, not the summary page." },
  { t: "One month of your telemetry export", d: "Including whatever machine-level DEX detail it gives. That one question decides whether lost-sales analysis is possible at all." },
  { t: "One bank statement for the same period", d: "So the money that arrived can be tied back to the sales that caused it." },
  { t: "A loaded hourly cost for a driver", d: "Five minutes of your time, and it unlocks every margin figure." },
]);

export default function LandingPage({ brand }: PageProps): React.JSX.Element {
  // Annotated rather than inferred: a bare object literal widens textAlign and
  // textTransform to `string`, which is not assignable to CSSProperties when
  // the const is later spread into a style prop.
  const wrap: React.CSSProperties = { maxWidth:1040, margin:"0 auto", padding:"0 24px", position:"relative" };
  const heading: React.CSSProperties = { fontFamily:"'Bebas Neue',cursive", color:"var(--text-strong)", letterSpacing:"0.02em" };
  const panel: React.CSSProperties = { background:"var(--surface)", border:"1px solid var(--border-subtle)", borderRadius:10, padding:"26px 22px" };
  const label: React.CSSProperties = { fontSize:11, color:"var(--text-faint)", letterSpacing:"0.14em", textTransform:"uppercase", fontWeight:700 };
  const ctaPrimary: React.CSSProperties = {
    display:"inline-block", background:brand.primary, color:"var(--on-accent)", borderRadius:6,
    padding:"14px 30px", fontSize:13, fontWeight:700, letterSpacing:"0.1em",
    textDecoration:"none", textTransform:"uppercase",
  };
  const ctaGhost: React.CSSProperties = {
    ...ctaPrimary, background:"transparent", color:"var(--text-strong)",
    border:"1px solid var(--border-strong)",
  };
  const band: React.CSSProperties = { position:"relative", borderTop:"1px solid var(--border-subtle)", padding:"58px 0" };
  const navLink: React.CSSProperties = { color:"var(--text-soft)", fontSize:12, fontWeight:700, letterSpacing:"0.08em", textDecoration:"none", textTransform:"uppercase" };

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", color:"var(--text)" }}>
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(var(--surface-raised) 1px,transparent 1px),linear-gradient(90deg,var(--surface-raised) 1px,transparent 1px)", backgroundSize:"44px 44px", opacity:0.4, pointerEvents:"none" }} />

      {/* ── Header ── */}
      <div style={{ ...wrap, display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, flexWrap:"wrap", padding:"20px 24px" }}>
        <div style={{ display:"inline-flex", alignItems:"baseline", gap:10 }}>
          <div style={{ ...heading, fontSize:30, color:brand.primary }}>{brand.name}</div>
          <div style={{ fontSize:12, color:"var(--text-faint)", letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:700 }}>Vending back office</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
          <a href="/notes" style={navLink}>Notes</a>
          <a href="/card-fees" style={navLink}>Fee calculator</a>
          <a href="#diagnostic" style={navLink}>Talk to us</a>
        </div>
      </div>

      {/* ── Hero ── */}
      <div style={{ ...wrap, textAlign:"center", padding:"66px 24px 76px" }}>
        <div style={{ fontSize:11, color:brand.primary, letterSpacing:"0.2em", fontWeight:700, textTransform:"uppercase", marginBottom:20 }}>For vending operators</div>
        <h1 style={{ ...heading, fontSize:"clamp(36px, 7.5vw, 60px)", lineHeight:1.04 }}>
          Your telemetry tells you what sold.<br />
          <span style={{ color:brand.primary }}>Nothing tells you what it earned.</span>
        </h1>
        <div style={{ fontSize:17, color:"var(--text-muted)", maxWidth:660, margin:"24px auto 34px", lineHeight:1.65 }}>
          {brand.name} is being built to work out what each vend actually makes you — after product, card fees, commission and the cost of the visit — and to check every commission statement you are sent. It will read the systems you already pay for; nothing gets ripped out. Until it ships, we do it by hand.
        </div>
        <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
          <a href="/card-fees" style={ctaPrimary}>Try the fee calculator</a>
          <a href="#diagnostic" style={ctaGhost}>Get a diagnostic</a>
        </div>
        <div style={{ fontSize:13, color:"var(--text-faint)", marginTop:18 }}>Free, no sign-up, nothing leaves your browser.</div>
      </div>

      {/* ── The three questions ── */}
      <div style={{ ...band, borderBottom:"1px solid var(--border-subtle)" }}>
        <div style={wrap}>
          <h2 style={{ ...heading, fontSize:"clamp(28px, 5vw, 38px)", textAlign:"center", maxWidth:720, margin:"0 auto 10px" }}>
            Three questions your current systems cannot answer
          </h2>
          <div style={{ fontSize:15, color:"var(--text-muted)", textAlign:"center", maxWidth:580, margin:"0 auto 38px", lineHeight:1.7 }}>
            Not because anything is broken. Because no vending software on the market is built to answer them.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:20 }}>
            {QUESTIONS.map((x, i) => (
              <div key={x.q} style={panel}>
                <div style={{ width:34, height:34, borderRadius:8, background:brand.primary, color:"var(--on-accent)", fontFamily:"'Bebas Neue',cursive", fontSize:21, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16 }}>{i + 1}</div>
                <h3 style={{ fontSize:16, fontWeight:700, color:"var(--text-strong)", marginBottom:10, lineHeight:1.35 }}>{x.q}</h3>
                <div style={{ fontSize:14, color:"var(--text-muted)", lineHeight:1.65 }}>{x.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── The calculator, as proof ── */}
      <div style={{ ...wrap, padding:"58px 24px" }}>
        <div style={{ ...panel, borderColor:brand.primary, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:26, alignItems:"center", padding:"30px 26px" }}>
          <div>
            <div style={{ ...label, color:brand.primary, marginBottom:12 }}>Free tool · no sign-up</div>
            <h2 style={{ ...heading, fontSize:"clamp(24px, 4vw, 32px)", lineHeight:1.15, marginBottom:12 }}>
              Half your card bill has nothing to do with how much you sell.
            </h2>
            <div style={{ fontSize:15, color:"var(--text-muted)", lineHeight:1.7 }}>
              It is the fixed charge on every tap, and on a small vend it weighs more than the percentage rate does. Put your own fleet in and see your split — then see what a fifty-cent bigger average ticket is worth. Thirty seconds, and the arithmetic runs in your browser.
            </div>
          </div>
          <div style={{ textAlign:"center" }}>
            <a href="/card-fees" style={ctaPrimary}>Open the calculator</a>
          </div>
        </div>
      </div>

      {/* ── What it does ── */}
      <div style={band}>
        <div style={wrap}>
          <h2 style={{ ...heading, fontSize:"clamp(28px, 5vw, 38px)", textAlign:"center", marginBottom:38 }}>What {brand.name} is being built to do</h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:20 }}>
            {PILLARS.map((p) => (
              <div key={p.t} style={{ ...panel, display:"flex", flexDirection:"column", gap:12 }}>
                <h3 style={{ ...heading, fontSize:24, color:brand.primary }}>{p.t}</h3>
                <div style={{ fontSize:14, color:"var(--text-muted)", lineHeight:1.65 }}>{p.d}</div>
                <div style={{ fontSize:13, color:"var(--text-soft)", lineHeight:1.6, borderLeft:`2px solid ${brand.primary}`, paddingLeft:12, marginTop:"auto" }}>
                  {p.e}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Objection: do I have to replace anything? ── */}
      <div style={band}>
        <div style={{ ...wrap, textAlign:"center" }}>
          <h2 style={{ ...heading, fontSize:"clamp(26px, 4.6vw, 36px)" }}>It sits on top of what you already have</h2>
          <div style={{ fontSize:16, color:"var(--text-muted)", maxWidth:680, margin:"16px auto 0", lineHeight:1.75 }}>
            Crane, Cantaloupe, Nayax, VendSoft, Parlevel, a shelf of spreadsheets — {brand.name} is built to read them, not to replace them. No changing telemetry provider, no new boards in the machines, no ripping out your route software. The whole point is that the data you need is already being collected and paid for; nobody is doing the arithmetic on it.
          </div>
        </div>
      </div>

      {/* ── The diagnostic ── */}
      <div id="diagnostic" style={{ ...band, borderTop:"1px solid var(--border-subtle)" }}>
        <div style={wrap}>
          <h2 style={{ ...heading, fontSize:"clamp(28px, 5vw, 40px)", textAlign:"center" }}>Find out what it is really costing you</h2>
          <div style={{ fontSize:16, color:"var(--text-muted)", maxWidth:640, margin:"16px auto 36px", lineHeight:1.75, textAlign:"center" }}>
            Send us four things and we will replace every estimate with your actual figures — your true effective card rate, the prices worth changing, which statements do not add up, and one bank deposit traced all the way back to the machines that produced it.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))", gap:22, marginBottom:38 }}>
            {DIAGNOSTIC.map((d, i) => (
              <div key={d.t}>
                <div style={{ fontSize:11, color:brand.primary, fontWeight:700, letterSpacing:"0.12em", marginBottom:8 }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 style={{ fontSize:15, fontWeight:700, color:"var(--text-strong)", marginBottom:7, lineHeight:1.4 }}>{d.t}</h3>
                <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.65 }}>{d.d}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ display:"flex", gap:56, justifyContent:"center", flexWrap:"wrap", marginBottom:26 }}>
              <div>
                <div style={{ ...label, marginBottom:8 }}>Call</div>
                <a href={brand.phoneHref} style={{ fontSize:26, fontWeight:700, color:brand.primary, textDecoration:"none" }}>{brand.phone}</a>
              </div>
              <div>
                <div style={{ ...label, marginBottom:8 }}>Email</div>
                <a href={`mailto:${brand.contactEmail}?subject=Diagnostic`} style={{ fontSize:26, fontWeight:700, color:brand.primary, textDecoration:"none" }}>{brand.contactEmail}</a>
              </div>
            </div>
            <div style={{ fontSize:13, color:"var(--text-faint)" }}>
              No obligation and no mailing list. If there is nothing worth chasing, that is a perfectly good answer and you will have it cheaply.
            </div>
          </div>
        </div>
      </div>

      <div style={{ position:"relative", borderTop:"1px solid var(--border-subtle)", padding:"22px 24px", textAlign:"center", fontSize:12, color:"var(--text-faint)" }}>
        {brand.copyright} &copy; {new Date().getFullYear()} &nbsp;·&nbsp;{" "}
        <a href={brand.phoneHref} style={{ color:"var(--text-muted)", textDecoration:"none" }}>{brand.phone}</a> &nbsp;·&nbsp;{" "}
        <a href={`mailto:${brand.contactEmail}`} style={{ color:"var(--text-muted)", textDecoration:"none" }}>{brand.contactEmail}</a>
        <div style={{ marginTop:8 }}>{brand.positioning}</div>
      </div>
    </div>
  );
}
